import { InterpolantFactoryMethodInOutQuad } from './erasedLinearInterpolant'
import Network from '../../miniviews/network';
import RenderManager from '../../rendering/render-manager';

class IsolationSequenceAnimation {
    constructor(slideIndex) {
        this.slideIndex = slideIndex;
    }

    start() {
        this.animationClip = createAnimationClip(this.slideIndex);
        this.animationAction = Q3.mixer.clipAction(this.animationClip);
        this.animationAction.loop = THREE.LoopOnce;
        this.animationAction.play();
    }

    kill() {
        if (this.animationAction) {
            this.animationAction.stop();
            Q3.mixer.uncacheClip(this.animationAction);
            this.animationAction = null;
        }
    }
}

export default IsolationSequenceAnimation;
export { IsolationSequenceAnimation };

function createAnimationClip(slideIndex) {
    this.tracks = [];
    const sequenceData = Q3.data.slides[slideIndex].isolationSequence;
    if (sequenceData) {
        sequenceData.meshesUuids.forEach((meshUuid, meshIndex) => {
            addMeshIsolationProperties(AssetLibrary.objects[meshUuid]);

            const trackOpacity = createMeshOpacityTrack(
                meshUuid,
                sequenceData.meshesUuids.length,
                meshIndex,
                sequenceData.opacity,
                sequenceData.fadeTime,
                sequenceData.stepTime
            );
            //turn on transparency for all meshes, will be automatically set bac to orginal value after animation is done
            const trackTransparent = new THREE.BooleanKeyframeTrack(meshUuid + ".isolationSequenceTransparent", [0], [true], THREE.InterpolateDiscrete);

            this.tracks.push(trackOpacity, trackTransparent);
        })
    }
    if (sequenceData.autoZoom) {
        const trackControlsTarget = createCameraTargetTrack(sequenceData.meshesUuids, sequenceData.fadeTime, sequenceData.stepTime);
        const trackCameraPos = createCameraPositionTrack(sequenceData.meshesUuids, sequenceData.fadeTime, sequenceData.stepTime);
        this.tracks.push(trackControlsTarget, trackCameraPos);
    }
    return new THREE.AnimationClip('isolation sequence', -1, this.tracks);
}

function createMeshOpacityTrack(partName, partsCount, partIndex, hideOpacity, fadeTime, partPresentationTime) {
    const times = new Float32Array((partsCount + 1) * 2);
    const values = new Float32Array((partsCount + 1) * 2);

    times[0] = 0;
    times[1] = fadeTime;
    for (let i = 0; i < partsCount + 1; i++) {
        times[i * 2] = i * (fadeTime + partPresentationTime);
        times[i * 2 + 1] = i * (fadeTime + partPresentationTime) + fadeTime;
    }

    values[0] = 1;
    values[values.length - 1] = 1;
    for (let i = 0; i < partsCount; i++) {
        values[i * 2 + 1] = partIndex === i ? 1 : hideOpacity;
        values[i * 2 + 2] = partIndex === i ? 1 : hideOpacity;
    }

    const track = new THREE.NumberKeyframeTrack(partName + ".isolationSequenceOpacity", times, values);
    track.createInterpolant = InterpolantFactoryMethodInOutQuad;
    return track;
}

function addMeshIsolationProperties(object) {
    delete object.isolationSequenceTransparent;
    delete object.isolationSequenceOpacity;

    let materials = ensureArray(object.material);
    let currentTransparent = materials[0].transparent;
    let currentOpacity = materials[0].opacity;

    Object.defineProperty(object, 'isolationSequenceTransparent', {
        configurable: true,
        get: () => currentTransparent,
        set: (value) => {
            currentTransparent = value;
            for (let material of materials) {
                material.transparent = value;
            }
        },
    });
    Object.defineProperty(object, 'isolationSequenceOpacity', {
        configurable: true,
        get: () => currentOpacity,
        set: (value) => {
            currentOpacity = value;
            for (let material of materials) {
                material.opacity = value;
            }
        },
    });
}

function createCameraTargetTrack(meshesUuids, fadeTime, partPresentationTime) {
    const partsCount = meshesUuids.length;

    const times = new Float32Array((partsCount + 1) * 2);
    times[0] = 0;
    times[1] = fadeTime;
    for (let i = 0; i < partsCount + 1; i++) {
        times[i * 2] = i * (fadeTime + partPresentationTime);
        times[i * 2 + 1] = i * (fadeTime + partPresentationTime) + fadeTime;
    }

    const values = new Float32Array((partsCount + 1) * 2 * 3);

    values[0] = Q3.controls.target.x;
    values[1] = Q3.controls.target.y;
    values[2] = Q3.controls.target.z;

    for (let i = 0; i < partsCount; i++) {
        const mesh = Q3.assets.objects[meshesUuids[i]];
        const position = mesh.geometry.boundingSphere.center.clone();
        position.applyMatrix4(mesh.matrixWorld)

        values[i * 6 + 3] = position.x;
        values[i * 6 + 4] = position.y;
        values[i * 6 + 5] = position.z;

        values[i * 6 + 6] = position.x;
        values[i * 6 + 7] = position.y;
        values[i * 6 + 8] = position.z;
    }

    values[values.length - 3] = Q3.controls.target.x;
    values[values.length - 2] = Q3.controls.target.y;
    values[values.length - 1] = Q3.controls.target.z;

    const track = new THREE.VectorKeyframeTrack("Q3.controls.target", times, values);
    track.createInterpolant = InterpolantFactoryMethodInOutQuad;
    return track;
}

function createCameraPositionTrack(meshesUuids, fadeTime, partPresentationTime) {
    const partsCount = meshesUuids.length;

    const times = new Float32Array((partsCount + 1) * 2);
    times[0] = 0;
    times[1] = fadeTime;
    for (let i = 0; i < partsCount + 1; i++) {
        times[i * 2] = i * (fadeTime + partPresentationTime);
        times[i * 2 + 1] = i * (fadeTime + partPresentationTime) + fadeTime;
    }

    const values = new Float32Array((partsCount + 1) * 2 * 3);

    values[0] = RenderManager.activeCamera.position.x;
    values[1] = RenderManager.activeCamera.position.y;
    values[2] = RenderManager.activeCamera.position.z;

    for (let i = 0; i < partsCount; i++) {
        const mesh = Q3.assets.objects[meshesUuids[i]];
        const sceneBoundingSphere = mesh.geometry.boundingSphere.clone();
        sceneBoundingSphere.applyMatrix4(mesh.matrixWorld)

        const newCamDist = sceneBoundingSphere.radius * 2.5 / Math.tan(RenderManager.activeCamera.fov / 180 * Math.PI);//3 : zooms out to more than the bounding sphere's radius

        const targetToCam = Q3.controls.target.clone().sub(RenderManager.activeCamera.position).setLength(newCamDist);
        const nextCamPos = sceneBoundingSphere.center.clone().sub(targetToCam);

        values[i * 6 + 3] = nextCamPos.x;
        values[i * 6 + 4] = nextCamPos.y;
        values[i * 6 + 5] = nextCamPos.z;

        values[i * 6 + 6] = nextCamPos.x;
        values[i * 6 + 7] = nextCamPos.y;
        values[i * 6 + 8] = nextCamPos.z;
    }

    values[values.length - 3] = RenderManager.activeCamera.position.x;
    values[values.length - 2] = RenderManager.activeCamera.position.y;
    values[values.length - 1] = RenderManager.activeCamera.position.z;

    const track = new THREE.VectorKeyframeTrack(RenderManager.activeCamera.uuid + ".position", times, values, THREE.InterpolateLinear);
    track.createInterpolant = InterpolantFactoryMethodInOutQuad;
    return track;
}